import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Award, TrendingUp, BarChart3 } from 'lucide-react';
import Navbar from '@/components/Navbar';
import MegaFooter from '@/components/MegaFooter';
import SEOHead from '@/components/SEOHead';
import { DemoModal } from '@/components/DemoModal';
import { useState } from 'react';

var CASE_STUDIES = [
  {
    industry: "Cement & Building Materials",
    brand: "Leading Cement Manufacturer",
    region: "Pan-India | 12 States",
    duration: "18 Months",
    title: "3x Dealer Retention Through AI-Powered Loyalty",
    executive_summary: "A leading cement manufacturer with a 40% annual dealer churn rate and zero visibility into secondary sales deployed ChannelLoyalty's multi-tier loyalty platform. Within 18 months, they achieved a 68% reduction in churn, 3.2x increase in repeat orders, and complete secondary sales visibility across 12 states.",
    challenge_points: [
      "40% dealer churn annually — dealers switching to competitors offering short-term cash incentives",
      "No visibility into secondary sales (sell-out data) beyond primary invoicing",
      "Scheme leakage exceeding 25% of trade promotion budget due to manual tracking",
      "Fragmented regional incentive programs with inconsistent execution",
      "No mechanism to reward loyalty behavior vs. pure volume-based billing"
    ],
    solution_points: [
      "Deployed multi-tier loyalty hierarchy: Super Stockist → Distributor → Dealer → Sub-Dealer",
      "QR-verified purchase tracking at every transaction point with instant validation",
      "AI-powered churn prediction engine identifying at-risk dealers 45 days in advance",
      "Instant UPI payouts replacing delayed cheque-based incentive cycles",
      "WhatsApp-native engagement for scheme communication and reward notifications"
    ],
    metrics: [
      { value: "68%", label: "Reduction in Dealer Churn", context: "From 40% to 12.8% annual churn" },
      { value: "3.2x", label: "Increase in Repeat Orders", context: "Within first 12 months" },
      { value: "94%", label: "Dealer App Adoption", context: "Achieved in 90 days of launch" },
      { value: "42%", label: "Secondary Sales Visibility", context: "From 0% to full coverage" },
    ],
  },
  {
    industry: "FMCG & Consumer Goods",
    brand: "National FMCG Brand",
    region: "North & West India | 8 States",
    duration: "12 Months",
    title: "Scaling Retailer Engagement to 50,000+ Outlets",
    executive_summary: "A national FMCG brand with a fragmented retailer network and zero digital touchpoints deployed ChannelLoyalty's WhatsApp-based loyalty platform. Scheme leakage dropped from 30% to 4%, and 50,000+ retailers were onboarded within 6 months — with a 22% increase in per-outlet revenue.",
    challenge_points: [
      "Fragmented retailer network with no digital engagement channel",
      "Scheme leakage exceeding 30% of total trade promotion budget",
      "No real-time data on retailer performance or scheme utilization",
      "Competitors outpacing with digital-first retailer incentive programs",
      "Manual scheme communication through sales team — inconsistent and delayed"
    ],
    solution_points: [
      "WhatsApp-based enrollment and engagement — zero app downloads required",
      "Scan-to-earn invoice verification with AI-powered OCR validation",
      "AI-driven scheme optimization — automatically adjusting rewards based on performance",
      "Real-time secondary sales tracking dashboard for regional managers",
      "Automated scheme communication via WhatsApp with regional language support"
    ],
    metrics: [
      { value: "50K+", label: "Retailers Onboarded", context: "Within 6 months of launch" },
      { value: "26%", label: "Scheme Leakage Reduction", context: "From 30% to 4%" },
      { value: "22%", label: "Per-Outlet Revenue Growth", context: "Year-over-year increase" },
      { value: "4.8x", label: "ROI on Loyalty Investment", context: "Achieved in Year 1" },
    ],
  },
  {
    industry: "Automotive & Auto Parts",
    brand: "Tier-1 Auto Parts Distributor",
    region: "South & East India | 6 States",
    duration: "14 Months",
    title: "Mechanic Loyalty Program Driving 2x Brand Preference",
    executive_summary: "A tier-1 auto parts distributor facing zero brand loyalty among mechanics and rising counterfeit concerns deployed ChannelLoyalty's influencer loyalty platform. QR-based product authentication combined with instant rewards drove a 200% increase in brand-specific demand and an 85% reduction in counterfeit incidents.",
    challenge_points: [
      "Mechanics and workshops had no brand loyalty — decisions driven by margin and availability",
      "Counterfeit products eroding market share and undermining brand trust",
      "No direct engagement channel with 15,000+ mechanics in the network",
      "Competitor brands offering untracked cash incentives through local distributors",
      "New product launches failing to gain workshop-level traction"
    ],
    solution_points: [
      "QR code verification on every product — dual benefit of authentication + rewards",
      "Instant reward credits for each verified scan, redeemable via UPI or catalog",
      "Gamified product education modules with certification badges",
      "Tiered loyalty structure: Bronze → Silver → Gold based on scan frequency and learning",
      "Seasonal bonus campaigns during festive periods for engagement spikes"
    ],
    metrics: [
      { value: "200%", label: "Increase in Brand Demand", context: "From mechanic recommendations" },
      { value: "85%", label: "Counterfeit Reduction", context: "Through QR authentication" },
      { value: "12K+", label: "Mechanics Enrolled", context: "78% monthly active users" },
      { value: "35%", label: "Premium SKU Sales Lift", context: "Driven by product education" },
    ],
  },
  {
    industry: "Paints & Home Decor",
    brand: "Premium Paints Company",
    region: "Pan-India | 15 States",
    duration: "16 Months",
    title: "Painter Education & Certification Driving 45% More Recommendations",
    executive_summary: "A premium paints company whose key influencers — painters — had no product training or brand loyalty implemented ChannelLoyalty's education-driven loyalty platform. Interactive training, certification badges, and recommendation tracking led to a 45% increase in painter-driven product recommendations and 60% reduction in cash-based scheme costs.",
    challenge_points: [
      "Painters are the primary influencers in purchase decisions but lacked product knowledge",
      "Competitor brands aggressively offering untracked cash incentives to painters",
      "No mechanism to measure or reward painter recommendations vs. volume",
      "New premium product lines failing to get painter endorsement at point of recommendation",
      "Regional language barriers limiting scheme communication effectiveness"
    ],
    solution_points: [
      "Interactive product learning platform with video modules in 8 regional languages",
      "Certification badges (Star Painter, Master Painter) with tiered benefits",
      "Recommendation tracking — linking painter activity to actual dealer purchases",
      "Instant UPI payouts for verified recommendations, replacing delayed cash incentives",
      "Community features: painter leaderboards, regional contests, annual recognition events"
    ],
    metrics: [
      { value: "45%", label: "Increase in Recommendations", context: "Painter-driven product advocacy" },
      { value: "8.5K", label: "Painters Certified", context: "On premium product lines" },
      { value: "60%", label: "Cash Scheme Cost Reduction", context: "Shift to digital rewards" },
      { value: "82", label: "Net Promoter Score", context: "From painter community" },
    ],
  },
  {
    industry: "Pharmaceutical & Agri-Science",
    brand: "Leading Crop Science Company",
    region: "Pan-India | 12 States",
    duration: "20 Months",
    title: "Channel Partner Loyalty Reducing Distributor Attrition by 55%",
    executive_summary: "A leading crop science company with a fragmented distributor network across 12 states and severe TDS compliance challenges deployed ChannelLoyalty's centralized loyalty platform. Automated TDS calculation, multi-tier rewards, and AI-powered territory optimization reduced distributor attrition by 55% and delivered a 4.6x ROI in Year 1.",
    challenge_points: [
      "Distributor network fragmented across 12 states with no unified scheme management",
      "TDS compliance nightmares — manual calculations leading to regulatory risk",
      "Seasonal demand fluctuations making fixed incentive structures ineffective",
      "No data-driven approach to territory planning or distributor performance optimization",
      "Competitor brands poaching top distributors with aggressive upfront incentives"
    ],
    solution_points: [
      "Centralized loyalty platform with role-based access for HQ, regional, and territory managers",
      "Auto-TDS calculation engine — compliant payouts with zero manual intervention",
      "Dynamic scheme engine adjusting rewards based on seasonality and crop cycles",
      "AI-powered territory optimization identifying underserved markets and growth pockets",
      "Multi-tier rewards: distributors, retailers, and agronomists on one platform"
    ],
    metrics: [
      { value: "55%", label: "Distributor Attrition Reduction", context: "Year-over-year improvement" },
      { value: "100%", label: "TDS Compliance Automation", context: "Across all payouts" },
      { value: "38%", label: "Fill Rate Improvement", context: "Through territory optimization" },
      { value: "4.6x", label: "ROI on Loyalty Investment", context: "Achieved in Year 1" },
    ],
  },
  {
    industry: "Electrical & Electronics",
    brand: "National Electrical Brand",
    region: "North & West India | 10 States",
    duration: "10 Months",
    title: "Electrician Engagement Program Scaling to 25,000+ Partners",
    executive_summary: "A national electrical brand with zero direct engagement with electricians — the hidden influencers in electrical product decisions — deployed ChannelLoyalty's WhatsApp-first influencer loyalty platform. Within 4 months, 25,000+ electricians were enrolled, driving a 28% revenue growth in influencer-driven product categories.",
    challenge_points: [
      "Electricians are the hidden influencers but had zero engagement channels",
      "No brand recall among electricians despite significant above-the-line advertising",
      "Competitor brands capturing electrician mindshare through unstructured cash schemes",
      "Product recommendations driven by local relationships, not brand quality or features",
      "No data on which electricians were driving actual product installations"
    ],
    solution_points: [
      "WhatsApp-based enrollment — zero friction, works on any smartphone",
      "Product scan rewards — scan QR on purchased products for instant credits",
      "Certification program: Certified Electrician badges with tier-based benefits",
      "Seasonal bonus campaigns during festive and construction season peaks",
      "Referral program: electricians earn bonus for recruiting peers into the network"
    ],
    metrics: [
      { value: "25K+", label: "Electricians Enrolled", context: "Within 4 months of launch" },
      { value: "3.8x", label: "Brand Recall Improvement", context: "Post-program measurement" },
      { value: "52%", label: "Consistent Recommendations", context: "Among enrolled electricians" },
      { value: "28%", label: "Revenue Growth", context: "In influencer-driven categories" },
    ],
  },
];

function MetricCard({ metric, idx }) {
  return (
    <div className="text-center" data-testid={"metric-card-" + idx}>
      <div className="text-3xl md:text-4xl font-bold text-white mb-1">{metric.value}</div>
      <div className="text-sm font-semibold text-white/90 mb-1">{metric.label}</div>
      <div className="text-xs text-white/60">{metric.context}</div>
    </div>
  );
}

function ChallengeList({ items }) {
  var rendered = [];
  for (var i = 0; i < items.length; i++) {
    rendered.push(
      <li key={i} className="flex items-start gap-3 text-sm text-gray-600 leading-relaxed">
        <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0"></span>
        <span>{items[i]}</span>
      </li>
    );
  }
  return <ul className="space-y-3">{rendered}</ul>;
}

function SolutionList({ items }) {
  var rendered = [];
  for (var i = 0; i < items.length; i++) {
    rendered.push(
      <li key={i} className="flex items-start gap-3 text-sm text-gray-600 leading-relaxed">
        <CheckCircle size={16} className="text-[#7CC326] mt-0.5 flex-shrink-0" />
        <span>{items[i]}</span>
      </li>
    );
  }
  return <ul className="space-y-3">{rendered}</ul>;
}

function MetricsList({ metrics }) {
  var rendered = [];
  for (var i = 0; i < metrics.length; i++) {
    rendered.push(<MetricCard key={i} metric={metrics[i]} idx={i} />);
  }
  return <div className="grid grid-cols-2 md:grid-cols-4 gap-6">{rendered}</div>;
}

function CaseStudySection({ cs, idx }) {
  var isAlt = idx % 2 === 1;
  return (
    <section className={isAlt ? 'py-16 bg-gray-50' : 'py-16 bg-white'} data-testid={"case-study-" + idx}>
      <div className="container max-w-5xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-[#2C39B8]/8 text-[#2C39B8] border border-[#2C39B8]/15">{cs.industry}</span>
            <span className="text-xs text-gray-400">{cs.region}</span>
            <span className="text-xs text-gray-400">{cs.duration}</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{cs.title}</h2>
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">{cs.brand}</p>
        </div>

        {/* Executive Summary */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8 shadow-sm">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Executive Summary</h3>
          <p className="text-gray-700 text-sm leading-relaxed">{cs.executive_summary}</p>
        </div>

        {/* Metrics Band */}
        <div className="bg-[#2C39B8] rounded-xl p-6 md:p-8 mb-8">
          <MetricsList metrics={cs.metrics} />
        </div>

        {/* Challenge & Solution */}
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-400"></span> The Challenge
            </h3>
            <ChallengeList items={cs.challenge_points} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#7CC326]"></span> Our Solution
            </h3>
            <SolutionList items={cs.solution_points} />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function CaseStudiesPage() {
  var [demoOpen, setDemoOpen] = useState(false);

  var caseStudySections = [];
  for (var i = 0; i < CASE_STUDIES.length; i++) {
    caseStudySections.push(<CaseStudySection key={i} cs={CASE_STUDIES[i]} idx={i} />);
  }

  return (
    <div data-testid="case-studies-page">
      <Navbar onDemoClick={function() { setDemoOpen(true); }} />
      <SEOHead
        title="Case Studies - B2B Channel Loyalty Success Stories | ChannelLoyalty"
        description="See how leading brands across Cement, FMCG, Automotive, Paints, Pharma, and Electrical industries transformed their channel loyalty with ChannelLoyalty's AI-powered platform."
        keywords="B2B loyalty case studies, dealer loyalty success stories, channel partner engagement results, loyalty program ROI India"
        canonical="https://channelloyalty.ai/case-studies"
      />

      {/* Banner Hero */}
      <section className="hero-banner-full">
        <div className="hero-banner-carousel">
          <div className="hero-banner-track">
            <div className="hero-banner-slide active">
              <img src="/images/banner_case_studies.jpg" alt="ChannelLoyalty Case Studies - Proven Results, Measurable Impact" />
            </div>
          </div>
        </div>
      </section>

      {/* Summary Metrics */}
      <div className="bg-[#2C39B8] py-5">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-2xl md:text-3xl font-bold text-white">6</div>
              <div className="text-xs text-white/60 mt-1">Industries Covered</div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-[#7CC326]">100K+</div>
              <div className="text-xs text-white/60 mt-1">Channel Partners Impacted</div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-white">4.2x</div>
              <div className="text-xs text-white/60 mt-1">Average ROI</div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-[#7CC326]">55%</div>
              <div className="text-xs text-white/60 mt-1">Avg. Churn Reduction</div>
            </div>
          </div>
        </div>
      </div>

      {/* Case Studies */}
      {caseStudySections}

      {/* CTA */}
      <section className="cta-section">
        <div className="container relative z-10 text-center">
          <h2>Want Results Like These?</h2>
          <p>See how ChannelLoyalty can transform your channel loyalty program.</p>
          <div className="cta-buttons" style={{ justifyContent: 'center' }}>
            <Link to="/contact" className="btn btn-primary">
              Schedule Demo <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <MegaFooter />
      <DemoModal isOpen={demoOpen} onClose={function() { setDemoOpen(false); }} />
    </div>
  );
}
